'use client'

import { useEffect, useState } from 'react'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
}

function detectIOS() {
  if (typeof navigator === 'undefined') return false

  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

function detectStandalone() {
  if (typeof window === 'undefined') return false

  const displayModeStandalone =
    window.matchMedia('(display-mode: standalone)').matches

  const navigatorStandalone =
    'standalone' in window.navigator &&
    Boolean((window.navigator as any).standalone)

  return displayModeStandalone || navigatorStandalone
}

export function usePWAInstall() {
  const [isInstalled, setIsInstalled] = useState(false)

  const [installPrompt, setInstallPrompt] =
    useState<BeforeInstallPromptEvent | null>(null)

  const [isIOS, setIsIOS] = useState(false)

  useEffect(() => {
    setIsInstalled(detectStandalone())
    setIsIOS(detectIOS())

    const media = window.matchMedia(
      '(display-mode: standalone)'
    )

    const handleDisplayModeChange = () => {
      setIsInstalled(detectStandalone())
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault()

      setInstallPrompt(
        event as BeforeInstallPromptEvent
      )
    }

    const handleInstalled = () => {
      setIsInstalled(true)
      setInstallPrompt(null)
    }

    media.addEventListener(
      'change',
      handleDisplayModeChange
    )

    window.addEventListener(
      'beforeinstallprompt',
      handleBeforeInstallPrompt
    )

    window.addEventListener(
      'appinstalled',
      handleInstalled
    )

    return () => {
      media.removeEventListener(
        'change',
        handleDisplayModeChange
      )

      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      )

      window.removeEventListener(
        'appinstalled',
        handleInstalled
      )
    }
  }, [])

  async function install() {
    if (!installPrompt) {
      return false
    }

    await installPrompt.prompt()

    const choice =
      await installPrompt.userChoice

    if (choice.outcome === 'accepted') {
      setInstallPrompt(null)
      return true
    }

    return false
  }

  return {
    isInstalled,
    isIOS,
    canPromptInstall: Boolean(installPrompt),
    install,
  }
}